import { AccessTimeOutlined, CalendarMonthOutlined, SportsGolf } from '@mui/icons-material';
import { Box, Fade, Stack, Typography } from '@mui/material';
import { ClubFittingType } from 'common/infrastructure/modules/appointment/interfaces/AvailabilityTypes';
import React from 'react';

import BookingCalendar from 'infrastructure/components/BookingCalendar';
import ButtonsGroup from 'infrastructure/components/ButtonsGroup';
import Loader from 'infrastructure/components/Loader';
import SectionTitle from 'infrastructure/components/SectionTitle';
import FittingTypeSelector from 'infrastructure/targets/web/modules/bookingWizard/Steps/ChooseATimeStep/components/FittingTypesButtonsGroup';

import { StyledButtonsContainer, StyledCalendarContainer, StyledLoaderContainer } from './style';

interface BookFittingWithInstructorProps {
  clubFittingTypes: ClubFittingType[] | undefined;
  fittingType: number | undefined;
  calendarLoadingInProgress: boolean;
  showStartTime: boolean;
  staffAvailableFittingDates: string[] | undefined;
  reservationDate: string | undefined;
  handleDateChange: (value: Date | null) => void;
  fetchFurtherAvailableDates: (newDate: Date | null) => void;
  availableAppointmentsError: string | undefined;
  areAppointmentsLoading: boolean;
  sessionHour: string;
  handleSessionHourChange: (value: string, event: React.MouseEvent<HTMLElement>) => Promise<void>;
  availableFittingSlotButtons: () =>
    | {
        value: string;
        title: string;
        secondaryValue: number | undefined;
      }[]
    | undefined;
  handleFittingTypeChange: (value: string, event: React.MouseEvent<HTMLElement>) => Promise<void>;
}

const BookFittingWithInstructor: React.FC<BookFittingWithInstructorProps> = ({
  clubFittingTypes,
  fittingType,
  calendarLoadingInProgress,
  showStartTime,
  staffAvailableFittingDates,
  reservationDate,
  handleDateChange,
  fetchFurtherAvailableDates,
  availableAppointmentsError,
  areAppointmentsLoading,
  sessionHour,
  handleSessionHourChange,
  availableFittingSlotButtons,
  handleFittingTypeChange,
}) => {
  const sortedFittingTypes = clubFittingTypes
    ? clubFittingTypes?.slice().sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const availableFittingSlotBusttonsList = availableFittingSlotButtons();
  const isFittingAvailable = !!staffAvailableFittingDates?.length;
  return (
    <>
      {sortedFittingTypes?.length ? (
        <>
          <Box sx={{ marginBottom: '20px' }}>
            <SectionTitle title={'Fitting Type'} icon={<SportsGolf />} />
            {Boolean(sortedFittingTypes?.length) && (
              <FittingTypeSelector
                onChange={handleFittingTypeChange}
                selected={fittingType}
                fittingTypes={sortedFittingTypes}
              />
            )}
          </Box>
          <SectionTitle
            title={'Select Date'}
            subtitle={!fittingType ? 'Select a fitting type to view available dates.' : undefined}
            icon={<CalendarMonthOutlined />}
          />
          {fittingType && (
            <>
              <StyledCalendarContainer>
                <Fade in={calendarLoadingInProgress} timeout={400}>
                  <StyledLoaderContainer>
                    <Loader />
                    <Typography variant="body2" color="text.secondary">
                      Fetching available days
                    </Typography>
                  </StyledLoaderContainer>
                </Fade>
                {isFittingAvailable ? (
                  <BookingCalendar
                    availableDates={staffAvailableFittingDates?.map((item) => new Date(item))}
                    selectedValue={reservationDate}
                    onChange={handleDateChange}
                    onMonthChange={fetchFurtherAvailableDates}
                    rental={false}
                  />
                ) : (
                  <Stack sx={{ my: 6 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center' }}>
                      Club fitting with this 5i coach is currently unavailable.
                    </Typography>
                  </Stack>
                )}
              </StyledCalendarContainer>
              {isFittingAvailable && (
                <>
                  <SectionTitle
                    title={'Start Time'}
                    subtitle={
                      !showStartTime ? 'Select a date to view available start times.' : undefined
                    }
                    icon={<AccessTimeOutlined />}
                  />
                  {Boolean(fittingType) && Boolean(reservationDate) && Boolean(showStartTime) && (
                    <>
                      <StyledButtonsContainer>
                        {availableAppointmentsError && !areAppointmentsLoading && (
                          <StyledLoaderContainer>
                            <Typography variant="body2">
                              Sorry, but there was an error while fetching available apointments,
                              please try again later
                            </Typography>
                          </StyledLoaderContainer>
                        )}

                        {areAppointmentsLoading ? (
                          <StyledLoaderContainer>
                            <Loader />
                            <Typography variant="body2">Fetching available appointments</Typography>
                          </StyledLoaderContainer>
                        ) : availableFittingSlotBusttonsList &&
                          availableFittingSlotBusttonsList.length > 0 ? (
                          <ButtonsGroup
                            buttonList={availableFittingSlotBusttonsList}
                            selectedValue={sessionHour}
                            onChange={handleSessionHourChange}
                          />
                        ) : (
                          <Typography variant="h6" sx={{ textAlign: 'center' }}>
                            There are currently no times available for this fitting type. Please
                            select another date.
                          </Typography>
                        )}
                      </StyledButtonsContainer>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      ) : (
        <Typography variant="h6" sx={{ textAlign: 'center' }}>
          Club fitting at this 5i location is currently unavailable.
        </Typography>
      )}
    </>
  );
};

export default BookFittingWithInstructor;
